import { cn } from '@/lib/cn';
import { es } from '@/i18n/es';

const SIZES = {
  xs: 'h-6 w-6 text-2xs',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-16 w-16 text-lg',
  xl: 'h-24 w-24 text-2xl',
} as const;

interface AvatarProps {
  name: string;
  photoURL?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

export function Avatar({ name, photoURL, size = 'md', className }: AvatarProps) {
  const classes = cn(
    'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-pill',
    'border border-line bg-surface-alt font-medium text-muted',
    SIZES[size],
    className,
  );

  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt={es.a11y.avatarOf(name)}
        loading="lazy"
        decoding="async"
        width={400}
        height={400}
        className={cn(classes, 'object-cover')}
      />
    );
  }

  return (
    <span className={classes} aria-hidden="true">
      {initials(name)}
    </span>
  );
}
